import React from 'react';
import { Helmet } from 'react-helmet-async';

import i18n from '../../i18n';
// @mui
import {
    Breadcrumbs,
    Button,
    ButtonGroup,
    Card,
    Table,
    Stack,
    Checkbox,
    Container,
    IconButton,
    MenuItem,
    Popover,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    Tooltip,
    Skeleton,
    Box,
    Typography
} from '@mui/material';
import {
    SearchOff as SearchOffIcon,
    FilterAltOff as FilterAltOffIcon,
    Inbox as InboxIcon
} from '@mui/icons-material';
// components
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
// sections
import { GridListHead, GridListToolbar } from '../../sections/@dashboard/grid';

import DialogAbstract from './DialogAbstract';
import Cells from '../cells';
//utils
import { adaptedName } from '../../utils/dataHelpers';
import TableSettingsDialog from '../dialogs/TableSettingsDialog';
import DetailsDialogAbstract from '../dialogs/DetailsDialog/DetailsDialogAbstract';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import config from '../../config';
import { formatFilterData, parseDate } from '../../utils/formatCalendar';
import DateTimePickerDialog from '../dialogs/DateTimePickerDialog';
import { GridTotalRow } from '../../sections/@dashboard/grid/total-row';
import { debounce } from 'lodash';
import { GridDataAdapter } from '../../adapter/DataAdapter';
import { formatBackendError, formatError } from '../../utils/formatError';
import { formatController } from '../../utils/formatName';
import {
    getVisibleFieldSet,
    isFieldInVisibleSet,
    isFieldVisibleByRole
} from '../../utils/isFieldVisible';
import { controllerSupportsArchive } from '../../config/archiveSupportConfig';
import { modifiedReasonConfig } from '../../config/modifiedReasonConfig';
import ModifiedDialog from '../dialogs/ModifiedDialog';
import CreateExportTaskDialog from '../dialogs/CreateExportTaskDialog';
import { EmptyStateInfoCard } from '../../components/EmptyStateInfoCard';
import { isCountDisabledFor } from '../../config/disableCount.config';
import { MAIN_CONTENT_OFFSET } from '../../layouts/dashboard/MainLayout';
import TablePaginationActionsWithDisable from '../../components/table/TablePaginationActionsWithDisable';
import AddIcon from '@mui/icons-material/Add';
import { lockSupportConfig } from '../../config/lockSupportConfig';
import LockInstanceDialog from '../dialogs/LockInstanceDialog';
import FilterService from '../../services/filterService/FilterService';
import { safeParseHeadFromStorage } from '../../utils/gridHeadStorage';
import {
    densityTableSx,
    getDensity,
    readDensity,
    writeDensity
} from '../../theme/density';
import { findMenuAncestors } from '../../utils/menuTrail';
import { hasReadPermission } from '../../utils/menuPermissions';
import { fNumber } from '../../utils/formatNumber';
import ActiveFilterChips from '../../sections/@dashboard/grid/ActiveFilterChips';
import { stickyActionCellSx } from '../../components/tableStyles';
import GridColumnResizeHandle from '../../sections/@dashboard/grid/GridColumnResizeHandle';
import GridColumnDragGrip from '../../sections/@dashboard/grid/GridColumnDragGrip';
import GridColumnLayoutUndo from '../../sections/@dashboard/grid/GridColumnLayoutUndo';
import { sizedColumnsTableSx } from '../../sections/@dashboard/grid/gridColumnSizing.styles';
import { gridColumnDndSx } from '../../sections/@dashboard/grid/gridColumnDnd.styles';
import { GRID_ACTIONS_CELL_PROPS } from '../../utils/gridColumns';
import {
    GRID_COLUMN_AUTO_FIT_MAX_WIDTH,
    EMPTY_COLUMN_LAYOUT,
    GRID_COLUMN_DEFAULT_WIDTH,
    applyColumnOrder,
    clampColumnWidth,
    clearColumnLayout,
    isEmptyColumnLayout,
    loadColumnLayout,
    localColumnLayoutStore,
    moveColumn,
    pruneColumnLayout,
    saveColumnLayout,
    setColumnWidths
} from '../../utils/gridColumnLayout';
import { startColumnDrag } from '../../utils/gridColumnDrag';
import { findHorizontalScroller } from '../../utils/gridDragAutoScroll';
import { measureColumnContentWidth } from '../../utils/gridColumnAutoFit';

// Where the user's column widths and order live: this browser's localStorage.
// A backend-backed store (per user) can take its place — the grid treats every
// store call as possibly asynchronous.
const columnLayoutStore = localColumnLayoutStore;

// Column resizing and reordering need room for the table and a fine pointer:
// on a phone or a touch screen the head is a strip to scroll, not to drag.
const WIDE_QUERY = '(min-width: 600px)';
const COARSE_POINTER_QUERY = '(pointer: coarse)';
const mediaMatches = (query) => Boolean(window.matchMedia?.(query)?.matches);
const columnLayoutAvailable = () =>
    mediaMatches(WIDE_QUERY) && !mediaMatches(COARSE_POINTER_QUERY);

/**
 * @typedef {Object} HeadObject
 * @property {string} id - Unique identifier.
 * @property {string} label - Object label.
 * @property {boolean} alignRight - Alignment to the right.
 * @property {string} cell - Type of cell and editor.
 * @property {boolean} displayGrid - Display in the grid.
 * @property {boolean} disableEditor - Disable editor.
 * @property {boolean} displayDialogDetails - Display details in the dialog.
 * @property {'TextDetails' || 'Json'} details - Type of details:
 * @property {number} [width] - Width (px) the column starts at when the user
 *   has sized other columns of the grid but not this one.
 * @property {number} [minWidth] - Narrowest the user may drag the column
 *   (default 64).
 * @property {number} [maxWidth] - Widest the user may drag the column
 *   (default 800).
 * @property {boolean} [disableResize] - The column keeps its width.
 * @property {boolean} [disableReorder] - The column cannot be dragged to
 *   another place (other columns may still move around it).
 */

export default class GridPageAbstract extends React.Component {
    _isMounted = false;

    List = [];
    // Head = [];
    Sort = '';
    Name = 'Grid';
    Server = null;
    MenuDisplayType = 'menu' || 'icon' || 'none';
    CheckboxCell = true;
    ButtonRow = false;
    NeedSearch = true;

    disableGlobalEditor = false;
    disableGlobalDelete = false;
    disableAdd = false;

    searchFields = [];
    searchOp = ':';

    showDatePicker = true;
    // if enabled: grid head field must have a cell from cellConfig.js and in head object must be a field 'total' with true value
    showTotalRow = false;

    exportButton = false;

    unsubscribe = () => {};
    customLogicOnMount() {}
    hasCustomLogicOnMount = false;
    customPermissionCreate = null;

    allowedTransferBalance = () => false;
    transferBalance = () => {};

    disableDateRange = {
        disabled: false,
        text: ''
    };

    dateRangeField = 'dateCreated';
    limitDateRange = 31; // days
    disableCount = false;

    // Column widths the user can drag on desktop. A page opts out by setting
    // this to false; a single column by `disableResize` in its Head entry.
    enableColumnResize = true;
    // Columns the user can reorder by dragging their head cell on desktop.
    // A page opts out by setting this to false; a single column by
    // `disableReorder` in its Head entry.
    enableColumnReorder = true;

    constructor(props) {
        super(props);
        this.tableViewportRef = React.createRef();
        this.state = {
            /**
             * Array of Head objects.
             * @type {HeadObject[]}
             */
            head: [],

            sortConfig: config.defaultSort,
            open: null,
            order: 'asc',
            selected: [],
            orderBy: 'name',
            searchValue: '',

            menuClickItemId: null,

            page: 0,
            rowsPerPage: 5,
            totalCount: 0,
            hasNextPage: true,

            dialogOpen: false,
            dialogData: {},

            isLoading: true,
            isDataLoading: false,
            filterRequest: [],

            dialogDetailsOpen: false,
            dialogSettingsOpen: false,
            selectedItem: {},

            confirmDialogOpen: false,
            confirmationText: '',

            openDateRange: false,
            dateRangeValue: '',

            modifiedDialogOpen: false,

            lockInstanceDialogOpen: false,

            permissions: this.props.context.getState().permissions || {},
            actions: this.props.context.getState().actions || {},
            controllersData:
                this.props.context.getState().controllersData || {},
            currentUserRole:
                this.props.context.getState().userInfo.role?.systemRole || '',
            csvExportEnableControllers:
                this.props.context.getState().csvExportEnableControllers || [],

            tableViewportWidth: 0,
            tableViewportHeight: 0,

            density: readDensity(),

            // Bumped to remount the filter controls; see handleRemoveFilters.
            filterEpoch: 0,

            /** @type {import('../../utils/gridColumnLayout').GridColumnLayout} */
            columnLayout: EMPTY_COLUMN_LAYOUT,
            // The last change to it, while it can be taken back.
            columnLayoutUndo: null,
            // Kept in step with the media queries; see watchColumnLayoutMedia.
            columnLayoutAvailable: columnLayoutAvailable()
        };
        this.debouncedSearch = debounce(this.retrieveData, 500);
        this.fs = new FilterService();
    }

    installHeaderActions() {
        const isPermissionCreate =
            this.allowedPermission('create') && !this.disableAdd;
        const permissionsNotLoaded = !this.hasPermissions();

        const isMobile = window.innerWidth < 600;

        const pageTitle = i18n.t(`pages.${this.Name}.title`);
        // Ancestors only — the page itself is the heading right below. A section
        // named like its page says nothing twice (in Russian Payout instances
        // sits under "Выплаты" and is called "Выплаты").
        const ancestors = findMenuAncestors(
            config.menu,
            this.props?.location?.pathname || window.location.pathname
        ).filter((item) => i18n.t(item.title) !== pageTitle);

        // The line is kept when there is no trail, so a top-level page's title
        // sits where every other page's does instead of jumping up.
        const breadcrumbNode = ancestors.length ? (
            <Breadcrumbs
                aria-label="breadcrumb"
                separator="/"
                sx={{
                    'typography': 'caption',
                    'color': 'text.secondary',
                    '& .MuiBreadcrumbs-separator': { mx: 0.75 }
                }}
            >
                {ancestors.map((item) => (
                    <span key={item.path || item.title}>
                        {i18n.t(item.title)}
                    </span>
                ))}
            </Breadcrumbs>
        ) : (
            <Box sx={{ typography: 'caption' }} aria-hidden>
                {'\u00A0'}
            </Box>
        );

        // -1 is what retrieveData stores for grids in disableCountConfig, where
        // the backend is not asked for a total at all.
        const { totalCount } = this.state;
        const countNode =
            totalCount >= 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {fNumber(totalCount)}
                </Typography>
            ) : null;

        const titleNode = (
            <Box sx={{ minWidth: 0 }}>
                {breadcrumbNode}
                <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography variant="h5" color={'text.primary'} noWrap>
                        {pageTitle}
                    </Typography>
                    {countNode}
                </Stack>
            </Box>
        );

        const addButton =
            isPermissionCreate && !permissionsNotLoaded ? (
                <Button
                    variant="contained"
                    onClick={() => this.dialogOpen()}
                    startIcon={<AddIcon />}
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                    {/* By breakpoint, not by the window width when the header
                        was installed: the label then fits whatever the width
                        becomes, and is the same on every page at that width. */}
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                        {i18n.t(`pages.${this.Name}.add`)}
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                        {i18n.t(`buttons.add`)}
                    </Box>
                </Button>
            ) : null;

        const extraActions = permissionsNotLoaded
            ? null
            : this.renderHeaderExtraActions(isMobile);

        const actionsNode = (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ flexGrow: 1, minWidth: 0 }}
            >
                {titleNode}
                {extraActions ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {extraActions}
                        {addButton}
                    </Stack>
                ) : (
                    addButton
                )}
            </Stack>
        );
        this.props?.setHeaderActions?.(actionsNode);
    }

    /**
     * Page-specific buttons placed before the Add button. Only asked once
     * permissions have loaded. The header is a snapshot, so a page whose buttons
     * change with its state has to call installHeaderActions() again.
     */
    renderHeaderExtraActions(isMobile) {
        return null;
    }

    uninstallHeaderActions() {
        this.props?.clearHeaderActions?.();
    }

    // utility to use setState with async/await
    setStateAsync(updater) {
        return new Promise((resolve) => {
            this.setState(updater, resolve);
        });
    }

    async componentDidMount() {
        this.disableCount = isCountDisabledFor(this.Name);

        this._isMounted = true;

        // Before any request goes out, so the first page of results already
        // excludes archived rows.
        this.applyDefaultArchiveFilter();

        loadColumnLayout(columnLayoutStore, this.Name).then((columnLayout) => {
            if (this._isMounted) this.setState({ columnLayout });
        });
        this.watchColumnLayoutMedia();

        this.updateTableViewportSize();
        window.addEventListener('resize', this.updateTableViewportSize);

        const { context } = this.props;

        this.unsubscribe = context.subscribe(() => {
            const newPermissions = context.getState().permissions;
            const newActions = context.getState().actions;
            const newControllersData = context.getState().controllersData;
            const currentRole = context.getState().userInfo.role;
            const newCsvExportEnableControllers =
                context.getState().csvExportEnableControllers;
            if (this.state.permissions !== newPermissions && this._isMounted) {
                this.setState(
                    {
                        permissions: newPermissions,
                        actions: newActions,
                        controllersData: newControllersData,
                        currentUserRole: currentRole.systemRole,
                        csvExportEnableControllers:
                            newCsvExportEnableControllers
                    },
                    () => this.installHeaderActions()
                );
            }
        });

        try {
            this.setState({ isDataLoading: false });

            await this.updateContextControllersData();

            // Arriving from another grid's link (GridLinkCell,
            // handleGoToLinkedGrid): search for what it names, in the search
            // box, so it is seen and cleared there like any search.
            const linkedSearch = this.props.location?.state?.searchValue;
            if (linkedSearch) {
                await this.search(String(linkedSearch));
            }

            if (this.hasCustomLogicOnMount) {
                await this.customLogicOnMount();
                console.log('Custom logic on mount executed');
            }

            await this.retrieveData();
        } catch (error) {
            console.error('Error in retrieveData:', error);
        } finally {
            if (this._isMounted) {
                this.setState({ isDataLoading: true }, () =>
                    this.installHeaderActions()
                );
            }
        }
    }

    componentWillUnmount() {
        this.columnDrag?.cancel();
        this.stopColumnLayoutMedia?.();
        this.unsubscribe();
        this._isMounted = false;
        window.removeEventListener('resize', this.updateTableViewportSize);
        this.uninstallHeaderActions();
    }

    updateTableViewportSize = () => {
        const el = this.tableViewportRef.current;
        if (!el) return;
        const width = el.clientWidth;
        const height = el.clientHeight;
        this.setState({
            tableViewportWidth: width,
            tableViewportHeight: height
        });
    };

    /**
     * The filters behind the rows on screen: the search box as `filter`, and the
     * archive toggle, date range and filter controls as `prefilter`. The list
     * request is built from this, so anything that has to match what the user
     * sees (a CSV export, say) should take it from here rather than re-collect it.
     */
    currentListFilters() {
        return {
            filter: this.state.filterRequest,
            prefilter: this.buildFinalPrefilterRequest(this.fs)
        };
    }

    currentListSort() {
        return this.Server.getSortField(
            this.state.sortConfig.field,
            this.state.sortConfig.direction || 'desc'
        );
    }

    async fetchFilteredData(pageSize = null) {
        const sort = this.currentListSort();
        const { filter, prefilter } = this.currentListFilters();

        return await this.Server.getAllFiltered(
            filter,
            sort,
            {
                paging: {
                    page: this.state.page,
                    pageSize: pageSize || this.state.rowsPerPage
                },
                prefilter,
                includeTotalCount: !this.disableCount
            }
        );
    }

    async updateContextControllersData() {
        try {
            const updates = {};
            let prefilter = [];
            try {
                await this.waitForPermissions();
                await this.waitForControllersData();
            } catch (error) {
                this.handleError(error);
                return;
            }
            const controllersData = this.state.controllersData;
            const controllers = new Set();
            this.Head.forEach((field) => {
                if (field.from && this.allowedPermission('read', field.from)) {
                    controllers.add(field.from);
                }
                // `optionsFrom` loads a list the field's editor offers without
                // making the field depend on it. `from` does both: the editor
                // dialog and the details dialog drop a field unless the role may
                // read all of its `from` — right for a Select, wrong for a gateway
                // settings row's settings, which must not vanish because the role
                // cannot read scraper scenarios. Loaded only when the permission
                // is actually listed, so a backend that predates the controller
                // is not asked for it.
                if (
                    field.optionsFrom &&
                    hasReadPermission(this.state.permissions, adaptedName(field.optionsFrom))
                ) {
                    controllers.add(field.optionsFrom);
                }
            });

            const fetchPromises = Array.from(controllers).map(async (from) => {
                // Keyed on the controller being queried, not on the head config's
                // `object` alias: publicGateways is referenced as `gateway`,
                // `payinGateway` and `payoutGateway`, so matching by alias left
                // archived gateways in most of the dropdowns.
                if (controllerSupportsArchive(from)) {
                    prefilter = [
                        {
                            field: 'isArchived',
                            op: '!=',
                            value: true
                        }
                    ];
                } else prefilter = [];

                const result = await this.props.context
                    .getState()
                    .server[from].getLight([], [], {
                        prefilter
                    });
                updates[from] = result.data;
            });

            await Promise.allSettled(fetchPromises);

            if (Object.keys(updates).length > 0) {
                this.props.context.update({
                    controllersData: {
                        ...controllersData,
                        ...updates
                    }
                });
            }
            this.installHeaderActions();
        } catch (error) {
            console.error(
                'An error occurred while updating context controllers data:',
                error
            );
        }
    }

    /**
     * Hide archived rows — the default view for any controller that has the
     * column. The toolbar's "show archived" toggle starts out off and flips this
     * through toggleArchived.
     *
     * Seeded from componentDidMount rather than the constructor: subclasses assign
     * this.Name after super(props) returns, so it cannot be read there yet.
     * Previously nothing seeded it at all, and archived rows were listed until
     * someone toggled the button twice.
     */
    applyDefaultArchiveFilter = () => {
        if (!controllerSupportsArchive(this.Name)) return;
        this.fs
            .clearGroup('prefilterBase')
            .addFilter('isArchived', '!=', true, 'prefilterBase', true);
    };

    // Kept on the page, not in the toolbar, so the chip row can say so too: the
    // only sign of the archived view used to be a dot on the overflow button.
    toggleArchived = async (isArchived) => {
        this.setState({ showingArchived: isArchived });
        if (isArchived) {
            this.fs
                .clearGroup('prefilterBase')
                .addFilter('isArchived', '=', true, 'prefilterBase', true);
        } else {
            this.applyDefaultArchiveFilter();
        }
        await this.retrieveData();
    };

    processListData(data) {
        const uniqueItems = new Set();
        this.List = data.filter((item) => {
            if (!item.id) {
                return false;
            }
            const isUnique = !uniqueItems.has(item.id);
            if (isUnique) uniqueItems.add(item.id);
            return isUnique;
        });
    }

    handleError(error) {
        this.props.context.update({
            alertError: { message: formatError(error) }
        });
    }

    async retrieveData() {

        this.setState({ isLoading: true });

        try {
            let result;

            if (this.showTotalRow) {
                result = await this.fetchFilteredData(1000);
            } else {
                result = await this.fetchFilteredData();
            }

            if (result.error !== 'OK') {
                this.handleError(result);
                return;
            }

            this.processListData(result.data);

            if (this.disableCount) {
                this.setState({
                    hasNextPage: result.has_next
                    // hasNextPage: false
                });
            } else if (this.state.totalCount === 0) {
                this.setState({
                    hasNextPage: false
                });
            }

            if (this.state.page === 0) {
                const totalCount = this.disableCount ? -1 : result.total_count;
                this.setState({ totalCount: totalCount });
            }
        } catch (error) {
            this.handleError(error);
        } finally {
            // Re-install so the record count beside the page title reflects the
            // fetch that just finished.
            this.setState({ isLoading: false }, () =>
                this.installHeaderActions()
            );
        }
    }

    async updatePermissionsData() {
        const data = await this.props.context
            .getState()
            .server.users.metaInfo();
        this.props.context.update({
            permissions: data.permissions,
            actions: data.actionPermissions
        });
        this.installHeaderActions();
    }

    hasPermissions() {
        return !!Object.keys(this.state.permissions).length;
    }
    hasControllersData() {
        return !!Object.keys(this.state.controllersData).length;
    }

    waitForControllersData = (timeout = 5000) => {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();

            const checkControllersData = () => {
                if (this.hasControllersData()) {
                    resolve();
                } else if (Date.now() - startTime > timeout) {
                    reject(new Error('Controllers data loading timed out'));
                } else {
                    setTimeout(checkControllersData, 50);
                }
            };
            checkControllersData();
        });
    };

    waitForPermissions = (timeout = 5000) => {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();

            const checkPermissions = () => {
                if (this.hasPermissions()) {
                    resolve();
                } else if (Date.now() - startTime > timeout) {
                    reject(new Error('Permissions loading timed out'));
                } else {
                    setTimeout(checkPermissions, 50);
                }
            };
            checkPermissions();
        });
    };

    allowedPermission = (action, controller = this.Name) => {
        const adaptedController = adaptedName(controller);
        const permission = this.hasPermissions()
            ? this.state.permissions?.[adaptedController]?.[action]
            : 'deny';
        return permission !== 'deny';
    };

    allowedAction = (action) => {
        if (!this.state.actions || Object.keys(this.state.actions).length === 0)
            return false;

        return this.state.actions[action] === 'allow';
    };

    allowedArchive = () => {
        return controllerSupportsArchive(this.Name) && this.allowedAction('archive');
    };

    allowedCsvExportEnableControllers = () => {
        const csvExportEnableControllers =
            this.state.csvExportEnableControllers;
        return csvExportEnableControllers.some(
            (item) => item === formatController(this.Name)
        );
    };

    allowedLock = () => {
        const allowed = this.allowedAction('lock-instances');
        const exists = lockSupportConfig.some((item) => item === this.Name);
        return allowed && exists;
    };

    shouldShowModifiedReasonDialog = () => {
        return modifiedReasonConfig.some((item) => item === this.Name);
    };

    handleExportDataToCsv = () => {
        this.setState({ createExportTaskDialogOpen: true });
    };

    buildFinalPrefilterRequest(fs) {
        const prefilterBase = fs.getFlatFiltersByGroup('prefilterBase');
        const prefilterDate = fs.getFlatFiltersByGroup('prefilterDate');
        const prefilterFilter = fs.getFlatFiltersByGroup('prefilterFilter');
        return [...prefilterBase, ...prefilterDate, ...prefilterFilter];
    }

    search = async (value) => {
        const fs = this.fs;
        const hasValue = value !== '';

        const op = this.searchOp || FilterService.OPERATORS.LIKE;

        if (!hasValue) {
            fs.clearGroup('search');
            const filterRequest = fs.getFiltersByGroup('search');
            this.setState({ filterRequest, searchValue: '' }, () =>
                this.retrieveData()
            );
            return;
        }

        // Only fields this role can see: the backend refuses a filter on one it
        // hides (a gateway user's merchantTransactionId), which would fail the
        // whole search rather than just that field.
        const searchGroups = this.searchFields
            .filter((field) => isFieldVisibleByRole(field, this.state.currentUserRole))
            .map((field) => {
                return [{ field: field, op, value: value }];
            });

        fs.clearGroup('search').addGroups(searchGroups, 'search');

        const filterRequest = fs.getFiltersByGroup('search');

        await this.setStateAsync({
            page: 0,
            filterRequest,
            searchValue: value
        });
    };

    handleSearch = async (value) => {
        console.log('handleSearch value:', value);
        await this.search(value);

        // if (this.searchOp === FilterService.OPERATORS.EQUAL) {
        //     // search if string is fully entered, or press Enter
        //     if (isEqSearchFullMatch(value)) {
        //         await this.retrieveData();
        //     }
        //     return;
        // }
        if (!value) {
            return;
        }
        await this.debouncedSearch();
    };
    handleSearchKeyDown = async (e) => {
        if (e.key !== 'Enter') return;

        const value = e.target.value;
        if (!value) return;

        await this.search(value);
        await this.retrieveData();
    };

    // filterItems = async (filters) => {
    //     const fs = this.fs;
    //
    //     if (!filters || filters.length === 0) {
    //         fs.clearGroup('filter');
    //         const filterRequest = fs.getFiltersByGroup('filter');
    //         await this.setStateAsync(
    //             { filterRequest, filterActive: false, page: 0 },
    //             () => this.retrieveData()
    //         );
    //         return;
    //     }
    //     fs.clearGroup('filter').addGroup(filters, 'filter');
    //
    //     const filterRequest = fs.getFiltersByGroup('filter');
    //     await this.setStateAsync({
    //         filterRequest,
    //         filterActive: true,
    //         page: 0
    //     });
    // };

    filterItems = async (filters) => {
        const fs = this.fs;

        fs.clearGroup('prefilterFilter');

        if (!filters || filters.length === 0) {
            await this.setStateAsync({
                filterActive: false,
                page: 0
            });
            return;
        }

        fs.addGroup(filters, 'prefilterFilter');

        await this.setStateAsync({
            filterActive: true,
            page: 0
        });
    };

    handleFilterItems = async (filters) => {
        await this.filterItems(filters);
        await this.retrieveData();
    };

    /**
     * Removal driven from the filter chips rather than from a filter control.
     *
     * The controls keep their selection in their own state and only read
     * `filters` when they mount, so clearing a filter from outside would leave
     * the dropdown still displaying it. Bumping the epoch remounts the cluster,
     * which re-reads the filters that remain.
     */
    handleRemoveFilters = async (remaining) => {
        // Filters first, then the remount: the other way round, the remounted
        // controls read the filter list before the chip's filter left it, and a
        // cleared select kept showing its value.
        await this.filterItems(remaining);
        await this.setStateAsync((prev) => ({
            filterEpoch: prev.filterEpoch + 1
        }));
        await this.retrieveData();
    };
    async dateRangeSave(previewDate) {
        const fs = this.fs;
        const { startDate, endDate } = parseDate(previewDate);

        const prefilterDateGroup = formatFilterData(
            startDate,
            endDate,
            this.dateRangeField
        ).map(({ field, op, value }) => ({ field, op, value }));

        fs.clearGroup('prefilterDate').addGroup(
            prefilterDateGroup,
            'prefilterDate'
        );

        await this.setStateAsync({
            openDateRange: false,
            dateRangeValue: previewDate
        });
    }
    async handleDateRangeSave(previewDate) {
        await this.dateRangeSave(previewDate);
        await this.retrieveData();
    }

    handleClearDateRange() {
        const fs = this.fs;
        fs.clearGroup('prefilterDate');
        this.setState({ dateRangeValue: '' }, () => this.retrieveData());
    }

    handleLockInstance(bool) {
        // open lock dialog
        this.setState({ lockInstanceDialogOpen: bool });
    }

    handleOpenMenu = (event, id) => {
        this.setState({ open: event.currentTarget, menuClickItemId: id });
    };

    handleCloseMenu() {
        this.setState({ open: null, menuClickItemId: null });
    }

    handleRequestSort = (sortField) => {
        this.setState((prevState) => {
            const { field, direction } = prevState.sortConfig;
            const isFieldChanged = field !== sortField;

            const newDirection = isFieldChanged
                ? 'asc'
                : direction === 'asc'
                    ? 'desc'
                    : direction === 'desc'
                        ? this.state.sortConfig.direction ===
                      config.defaultSort.direction &&
                  this.state.sortConfig.field === config.defaultSort.field
                            ? 'asc'
                            : null
                        : 'asc';

            const newField = isFieldChanged
                ? sortField
                : newDirection === null
                    ? config.defaultSort.field
                    : sortField;

            return {
                sortConfig: {
                    field: newField,
                    direction: newDirection
                }
            };
        }, this.retrieveData);
    };

    handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = this.List.map((n) => n.id);
            this.setState({ selected: newSelected });
            return;
        }
        this.setState({ selected: [] });
    };

    handleClick = (event, name) => {
        const selectedIndex = this.state.selected.indexOf(name);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(this.state.selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(this.state.selected.slice(1));
        } else if (selectedIndex === this.state.selected.length - 1) {
            newSelected = newSelected.concat(this.state.selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                this.state.selected.slice(0, selectedIndex),
                this.state.selected.slice(selectedIndex + 1)
            );
        }
        this.setState({ selected: newSelected });
    };

    handleChangePage = (event, newPage) => {
        this.setState({ page: newPage }, () => this.retrieveData());
    };

    handleChangeRowsPerPage = (event) => {
        this.setState(
            { page: 0, rowsPerPage: parseInt(event.target.value, 10) },
            () => this.retrieveData()
        );
    };

    handleEditItem = (event) => {
        const data = this.List.filter(
            (item) => item.id === this.state.menuClickItemId
        ).at(0);

        this.dialogOpen(data);
    };

    handleShowDialogDetails = (event) => {
        const data = this.List.filter(
            (item) => item.id === this.state.menuClickItemId
        ).at(0);
        this.setState({
            dialogDetailsOpen: true,
            selectedItem: data
        });
        this.handleCloseMenu();
    };
    handleGoToLinkedGrid(event, gridName, foreignKey) {
        let id = null;
        const data = this.List.filter(
            (item) => item.id === this.state.menuClickItemId
        ).at(0);
        if (data && data[foreignKey]) {
            id = data[foreignKey];
        }
        this.props.navigate(`/app/${gridName}`, { state: { searchValue: id } });
        this.handleCloseMenu();
    }

    handleCloseDialogDetails = () => {
        this.setState({
            dialogDetailsOpen: false,
            selectedItem: {}
        });
    };

    handleDeleteItem = async (event) => {
        // single delete from right click menu
        // or multi deleted from selected by checkbox items
        let result = await this.Server.delete(
            this.state.menuClickItemId
                ? [this.state.menuClickItemId]
                : this.state.selected
        );
        this.handleCloseConfirmDialog();
        if (result.error !== 'OK') {
            this.props.context.update({
                alertError: { message: result.error }
            });
        } else {
            await this.retrieveData([]);
        }
    };

    handleRefreshStatus(event) {
        const id = this.state.menuClickItemId;
        this.props.context.update({
            alertError: {
                message: 'Updating...',
                type: 'snackbar',
                severity: 'info'
            }
        });
        this.Server.refreshStatus(id).then((res) => {
            if (res.error === 'OK') {
                this.props.context.update({
                    alertError: {
                        message: `Update successfully, status: ${res.data}`,
                        type: 'snackbar',
                        severity: 'success'
                    }
                });
                this.retrieveData();
            } else {
                this.props.context.update({
                    alertError: {
                        message: formatError(res),
                        type: 'snackbar',
                        severity: 'error'
                    }
                });
            }
        });
        this.handleCloseMenu();
    }

    handleArchive = async () => {
        this.setState({ loading: true });

        const ids = this.state.menuClickItemId
            ? [this.state.menuClickItemId]
            : this.state.selected;

        try {
            const result = await this.Server.archive(ids);
            this.handleCloseConfirmDialog();
            if (result?.error === 'OK') {
                this.props.context.update({
                    alertError: {
                        message: i18n.t('errors.archiveSuccess'),
                        type: 'snackbar',
                        severity: 'success'
                    }
                });
                await this.retrieveData();
            } else {
                this.handleError(result);
            }
        } catch (error) {
            this.handleError(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    handleSingleDeleteConfirmDialogOpen = (event) => {
        this.setState({
            open: false,
            confirmDialogOpen: true,
            // The page's own sentence, else a generic one rather than the raw key.
            confirmationText: i18n.t([
                `dialogs.${this.Name}ConfirmationSingleDelete`,
                'dialogs.confirmationSingleDelete'
            ]),
            confirmActionType: 'delete'
        });
    };
    handleMultiDeleteConfirmDialogOpen = (event) => {
        this.setState({
            confirmDialogOpen: true,
            confirmationText: i18n.t([
                `dialogs.${this.Name}ConfirmationMultipleDeleteText`,
                'dialogs.confirmationMultipleDeleteText'
            ]),
            confirmActionType: 'delete'
        });
    };

    handleArchiveConfirmDialogOpen = (event) => {
        this.setState({
            confirmDialogOpen: true,
            confirmationText: i18n.t(`dialogs.confirmationArchiveText`),
            confirmActionType: 'archive',
            open: false
        });
    };

    handleCloseConfirmDialog = () => {
        this.setState({
            confirmDialogOpen: false,
            menuClickItemId: null,
            selected: []
        });
    };

    handleOpenDateRange = () => {
        // open date range picker
        this.setState({ openDateRange: true });
    };

    handleChangeRangeDateField = (dateRange) => {
        this.setState({ page: 0 });
        this.setState({ dateRangeValue: dateRange });
    };

    handleOpenDialogSettings = () => {
        this.setState({ dialogSettingsOpen: true });
    };

    handleCloseDialogSettings() {
        this.setState({ dialogSettingsOpen: false });
    }

    handleTableSettingsChange() {
        const head = safeParseHeadFromStorage(this.Name);
        // The settings are saved against the full Head: a good moment to drop
        // layout entries of columns the grid no longer has.
        const { columnLayout } = this.state;
        const prunedLayout = pruneColumnLayout(
            columnLayout,
            head.map((field) => field.id)
        );
        this.setState({
            head: head,
            dialogSettingsOpen: false,
            columnLayout: prunedLayout
        });
        if (prunedLayout !== columnLayout) {
            saveColumnLayout(columnLayoutStore, this.Name, prunedLayout);
        }
    }

    handleDensityChange = (density) => {
        writeDensity(density);
        this.setState({ density });
    };
    getMenuClickItem() {
        const itemId = this.state.menuClickItemId;
        return this.List.find((item) => item.id === itemId);
    }
    getMenuClickItemValue(field) {
        const item = this.getMenuClickItem();
        return item ? item[field] : null;
    }
    handleAction = async (event, id, actionType) => {
        this.setState({ menuClickItemId: id }, () => {
            switch (actionType) {
            case 'edit':
                this.handleEditItem(event);
                break;
            case 'delete':
                this.handleSingleDeleteConfirmDialogOpen();
                break;
            default:
                break;
            }
        });
    };

    getRowsPerPageOptions = ({
        totalCount,
        hasNextPage,
        currentLen,
        rowsPerPage
    }) => {
        const baseOptions = [5, 10, 25];

        const ensureCurrentPresent = (opts) => {
            if (!opts.includes(rowsPerPage)) {
                const withCurrent = [...opts, rowsPerPage].sort(
                    (a, b) => a - b
                );
                return withCurrent.filter(
                    (v, i) => i === 0 || withCurrent[i - 1] !== v
                );
            }
            return opts;
        };

        const buildByCeil = (value) => {
            const ceil = baseOptions.find((v) => v >= value);
            // eslint-disable-next-line no-eq-null
            if (ceil == null) {
                return [...baseOptions];
            }
            return baseOptions.filter((v) => v <= ceil);
        };

        if (typeof totalCount === 'number' && totalCount >= 0) {
            const opts = buildByCeil(totalCount);
            return ensureCurrentPresent(opts);
        }

        if (hasNextPage === true) {
            return ensureCurrentPresent([...baseOptions]);
        }

        const opts = buildByCeil(currentLen || 0);
        return ensureCurrentPresent(opts);
    };

    renderCustomExtraIcons() {
        return <></>;
    }

    renderMenuCell(id) {
        // A 48px "large" button cannot fit a 34px compact row without forcing
        // it taller, which would defeat the density setting entirely.
        const menuButtonSize =
            this.state.density === 'comfortable' ? 'large' : 'small';
        return (
            <TableCell
                key="menu"
                align="right"
                sx={stickyActionCellSx}
                {...GRID_ACTIONS_CELL_PROPS}
            >
                {this.hasMenuItems(id) && (
                    <IconButton
                        size={menuButtonSize}
                        color="inherit"
                        onClick={(event) => this.handleOpenMenu(event, id)}
                    >
                        <Iconify icon={'eva:more-vertical-fill'} />
                    </IconButton>
                )}
            </TableCell>
        );
    }

    renderIconCell(id) {
        const row = this.rowById(id);
        const isPermissionUpdate =
            !!Object.keys(this.state.permissions).length &&
            this.allowedPermission('update') &&
            this.rowEditable(row);
        const isPermissionDelete =
            !!Object.keys(this.state.permissions).length &&
            this.allowedPermission('delete') &&
            this.rowDeletable(row);
        return (
            <TableCell key="menu" align="left" {...GRID_ACTIONS_CELL_PROPS}>
                {this.hasMenuItems(id) && (
                    <ButtonGroup
                        sx={{ display: 'flex', justifyContent: 'flex-start' }}
                    >
                        {isPermissionUpdate && (
                            <Tooltip title={i18n.t('common.edit')}>
                                <IconButton
                                    onClick={(event) =>
                                        this.handleAction(event, id, 'edit')
                                    }
                                >
                                    <Iconify icon={'eva:edit-fill'} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {isPermissionDelete && (
                            <Tooltip title={i18n.t('common.delete')}>
                                <IconButton
                                    onClick={(event) =>
                                        this.handleAction(event, id, 'delete')
                                    }
                                    sx={{ color: 'error.main' }}
                                >
                                    <Iconify icon={'eva:trash-2-outline'} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {this.renderCustomExtraIcons(id)}
                    </ButtonGroup>
                )}
            </TableCell>
        );
    }

    getCellValue = (row, field) => {
        if (field.array) return row[field.array];
        if (!field.object) {
            return row[field.id];
        }

        const baseObject = row?.[field.object];

        if (field.nestedObject) {
            const nestedObject = baseObject?.[field.nestedObject];

            if (field.secondaryObject) {
                const secondaryName = baseObject?.[field.secondaryObject]?.name;
                return { ...nestedObject, secondaryName };
            }

            return nestedObject;
        }

        return baseObject;
    };

    renderCustomGridCell() {
        return <></>;
    }

    /**
     * The sticky ⋮ column is drawn only when some row has a menu. Grids whose
     * rows have none (Roles actions permissions, events, URL callbacks)
     * carried an empty 67px column pinned to the right.
     */
    showsMenuColumn() {
        return (
            this.MenuDisplayType !== 'none' &&
            this.List.some((row) => this.hasMenuItems(row.id))
        );
    }

    hasMenuItems(id) {
        const customMenuItems = this.renderCustomMenuItems(id);
        const row = this.rowById(id);
        const isPermissionUpdate =
            this.allowedPermission('update') && !this.disableGlobalEditor && this.rowEditable(row);
        const isPermissionDelete =
            this.allowedPermission('delete') && !this.disableGlobalDelete && this.rowDeletable(row);

        return (
            isPermissionUpdate ||
            isPermissionDelete ||
            (Array.isArray(customMenuItems) && customMenuItems.length > 0)
        );
    }

    // Columns of the desktop table, in Head order: those the page shows in
    // the grid and the role may see. The head and the totals row filter by
    // the same rule, so every row lines up with them.
    getVisibleFields() {
        return this.state.head.filter((f) => {
            const fieldKey = f.cell === 'SelectObject' ? f.object : f.id;
            return (
                f.displayGrid &&
                isFieldVisibleByRole(fieldKey, this.state.currentUserRole)
            );
        });
    }

    // Resize and reorder controls come and go with the window width and the
    // pointer (a keyboard attached to a tablet) without waiting for some other
    // re-render to notice.
    watchColumnLayoutMedia() {
        const lists = [WIDE_QUERY, COARSE_POINTER_QUERY]
            .map((query) => window.matchMedia?.(query))
            .filter(Boolean);
        const update = () => {
            const available = columnLayoutAvailable();
            if (this._isMounted && available !== this.state.columnLayoutAvailable) {
                this.setState({ columnLayoutAvailable: available });
            }
        };
        lists.forEach((list) => list.addEventListener?.('change', update));
        this.stopColumnLayoutMedia = () =>
            lists.forEach((list) => list.removeEventListener?.('change', update));
    }

    canCustomizeColumns() {
        return this.state.columnLayoutAvailable;
    }

    isColumnResizeEnabled() {
        return this.enableColumnResize && this.canCustomizeColumns();
    }

    isColumnReorderEnabled() {
        return this.enableColumnReorder && this.canCustomizeColumns();
    }

    // Whether the user may size or move this grid's columns at all.
    isColumnLayoutEnabled() {
        return this.isColumnResizeEnabled() || this.isColumnReorderEnabled();
    }

    // The user has sized or moved columns: there is a layout to reset.
    isColumnLayoutCustomized() {
        return (
            this.isColumnLayoutEnabled() &&
            !isEmptyColumnLayout(this.state.columnLayout)
        );
    }

    saveColumnLayout = () =>
        saveColumnLayout(columnLayoutStore, this.Name, this.state.columnLayout);

    // Back to the Head's column order and natural widths.
    handleResetColumnLayout = () => {
        this.offerColumnLayoutUndo(
            this.state.columnLayout,
            'reset',
            i18n.t('common.columnLayoutReset')
        );
        this.setState({ columnLayout: EMPTY_COLUMN_LAYOUT });
        clearColumnLayout(columnLayoutStore, this.Name);
    };

    // Column title as the head shows it, for messages about the column.
    getColumnTitle(id) {
        const field = this.state.head.find((f) => f.id === id);
        if (!field) return id;
        return i18n.t(`columns.${field.labelCell || field.label}`);
    }

    // Offers to take a change to the column layout back, to `previous`.
    // Repeated changes of one kind to one column (`scope`) — arrow-key
    // steps, say — fold into one: undo goes back to before the first.
    offerColumnLayoutUndo(previous, scope, message) {
        this.setState((prev) => {
            const open = prev.columnLayoutUndo;
            return {
                columnLayoutUndo: {
                    key: Date.now(),
                    scope,
                    message,
                    layout: open?.scope === scope ? open.layout : previous
                }
            };
        });
    }

    handleColumnLayoutUndo = () => {
        const undo = this.state.columnLayoutUndo;
        if (!undo) return;
        this.setState(
            { columnLayout: undo.layout, columnLayoutUndo: null },
            this.saveColumnLayout
        );
    };

    handleColumnLayoutUndoClose = () => {
        this.setState({ columnLayoutUndo: null });
    };

    /*
     * Widths of the given (visible) columns, or null while the table keeps its
     * natural widths — until the user first drags a column edge of this grid.
     * From then on every visible column has a width: a column without a stored
     * one (shown later through table settings) gets its Head `width` or the
     * default.
     */
    getColumnWidths(fields) {
        const { widths } = this.state.columnLayout;
        if (!this.isColumnResizeEnabled() || !Object.keys(widths).length) {
            return null;
        }
        return fields.map((field) =>
            clampColumnWidth(
                widths[field.id] ?? field.width ?? GRID_COLUMN_DEFAULT_WIDTH,
                field
            )
        );
    }

    /*
     * A drag starts on a column edge. The first drag on a grid with natural
     * widths freezes every visible column at its current width, so sizing one
     * column leaves the others where they are. Returns the column's width.
     */
    handleColumnResizeStart = (id, cell) => {
        this.isResizingColumn = true;
        this.layoutBeforeResize = this.state.columnLayout;
        const fields = this.getVisibleFields();
        const field = fields.find((f) => f.id === id);
        const width = clampColumnWidth(cell.getBoundingClientRect().width, field);

        if (!Object.keys(this.state.columnLayout.widths).length) {
            const frozen = {};
            cell.closest('thead')
                .querySelectorAll('th[data-col-id]')
                .forEach((th) => {
                    const column = fields.find((f) => f.id === th.dataset.colId);
                    if (column) {
                        frozen[column.id] = clampColumnWidth(
                            th.getBoundingClientRect().width,
                            column
                        );
                    }
                });
            this.setState((prev) => ({
                columnLayout: setColumnWidths(prev.columnLayout, frozen)
            }));
        }
        return width;
    };

    getColumnElements(id) {
        const viewport = this.tableViewportRef.current;
        const selector = `[data-col-id="${CSS.escape(id)}"]`;
        return {
            col: viewport?.querySelector(`col${selector}`),
            headCell: viewport?.querySelector(`th${selector}`)
        };
    }

    // Live width while dragging: written straight to the <col>, so a drag does
    // not re-render the whole grid every frame. State follows on drag end.
    handleColumnResize = (id, width) => {
        const { col } = this.getColumnElements(id);
        if (col) col.style.width = `${width}px`;
    };

    // The head cell does not get narrower than the longest word of its label
    // (see sizedColumnsTableSx), however far left the edge was dragged: keep
    // the width the column actually has, measured with no width of its own.
    handleColumnResizeEnd = (id, dragWidth) => {
        this.isResizingColumn = false;
        let width = dragWidth;
        const { col, headCell } = this.getColumnElements(id);
        if (col && headCell) {
            // Collapsing the column may clamp the scroll position: put it back.
            const scroller = findHorizontalScroller(headCell);
            const scrollLeft = scroller?.scrollLeft;
            col.style.width = '0px';
            width = Math.max(
                width,
                Math.ceil(headCell.getBoundingClientRect().width)
            );
            col.style.width = `${width}px`;
            if (scroller) scroller.scrollLeft = scrollLeft;
        }
        const before = this.layoutBeforeResize;
        if (before?.widths[id] !== width) {
            this.offerColumnLayoutUndo(
                before,
                `width:${id}`,
                i18n.t('common.columnWidthChanged', {
                    column: this.getColumnTitle(id)
                })
            );
        }
        this.setState(
            (prev) => ({
                columnLayout: setColumnWidths(prev.columnLayout, { [id]: width })
            }),
            this.saveColumnLayout
        );
    };

    // Double-click on a column edge: as wide as its content on this page,
    // within GRID_COLUMN_AUTO_FIT_MAX_WIDTH and the column's own limits.
    handleColumnAutoFit = (id, cell) => {
        this.handleColumnResizeStart(id, cell);
        // Measured once the widths are frozen: sized columns lift the cap
        // text cells keep while widths are natural.
        this.setState({}, () => {
            const field = this.getVisibleFields().find((f) => f.id === id);
            const width = clampColumnWidth(
                Math.min(
                    measureColumnContentWidth(cell),
                    GRID_COLUMN_AUTO_FIT_MAX_WIDTH
                ),
                field
            );
            this.handleColumnResize(id, width);
            this.handleColumnResizeEnd(id, width);
        });
    };

    // Escape mid-drag: the layout as it was before the drag (the first drag
    // froze every width — that goes too).
    handleColumnResizeCancel = () => {
        this.isResizingColumn = false;
        this.setState({ columnLayout: this.layoutBeforeResize });
    };

    // Head entries in the user's column order — the desktop table only; the
    // edit and details dialogs and exports keep the Head's.
    getOrderedHead() {
        return this.isColumnReorderEnabled()
            ? applyColumnOrder(this.state.head, this.state.columnLayout.order)
            : this.state.head;
    }

    // Visible fields in table order: head, body rows, totals and <colgroup>
    // of the desktop table all follow it.
    getTableFields() {
        return this.isColumnReorderEnabled()
            ? applyColumnOrder(
                this.getVisibleFields(),
                this.state.columnLayout.order
            )
            : this.getVisibleFields();
    }

    // A press on a head cell may become a column drag (see gridColumnDrag.js);
    // the new order is committed on release.
    handleColumnPointerDown = (event) => {
        if (event.button !== 0 || this.isResizingColumn || this.columnDrag) {
            return;
        }
        this.columnDrag = startColumnDrag(event, {
            onMove: this.moveColumnTo,
            onEnd: () => {
                this.columnDrag = null;
            }
        });
    };

    // Puts column `fromId` before or after `toId`; resolves once the grid
    // has re-rendered in the new order.
    moveColumnTo = (fromId, toId, side) =>
        new Promise((resolve) => {
            const { order } = this.state.columnLayout;
            const nextOrder = moveColumn(
                this.state.head,
                order,
                fromId,
                toId,
                side
            );
            if (nextOrder === order) {
                resolve();
                return;
            }
            this.offerColumnLayoutUndo(
                this.state.columnLayout,
                `move:${fromId}`,
                i18n.t('common.columnMoved', {
                    column: this.getColumnTitle(fromId)
                })
            );
            this.setState(
                (prev) => ({
                    columnLayout: { ...prev.columnLayout, order: nextOrder }
                }),
                () => {
                    resolve();
                    this.saveColumnLayout();
                }
            );
        });

    // Alt+Shift+← / → on anything focused in a head cell (its sort label or
    // resize edge) moves the column one place; focus stays with it.
    handleColumnKeyDown = (event) => {
        const step = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
        if (!step || !event.altKey || !event.shiftKey) return;
        event.preventDefault();
        event.stopPropagation();
        const { colId: id } = event.currentTarget.dataset;
        const fields = this.getTableFields();
        const neighbour = fields[fields.findIndex((f) => f.id === id) + step];
        if (!neighbour) return;
        const onEdge =
            document.activeElement?.getAttribute('role') === 'separator';
        this.moveColumnTo(id, neighbour.id, step > 0 ? 'after' : 'before').then(
            () => {
                const cell = this.getColumnElements(id).headCell;
                const focusable = onEdge
                    ? cell?.querySelector('[role="separator"]')
                    : cell?.querySelector('[tabindex="0"]');
                focusable?.focus();
            }
        );
    };

    // Passed to the grid head, which spreads it on each column's head cell.
    getColumnDragProps = (headCell) => {
        if (!this.isColumnReorderEnabled() || headCell.disableReorder) {
            return {};
        }
        return {
            'data-reorderable': true,
            'aria-keyshortcuts': 'Alt+Shift+ArrowLeft Alt+Shift+ArrowRight',
            'onPointerDown': this.handleColumnPointerDown,
            'onKeyDown': this.handleColumnKeyDown
        };
    };

    // Passed to the grid head, which places them in each column's head
    // cell: the drag grip and the resize handle.
    renderColumnControls = (headCell) => {
        const reorderable =
            this.isColumnReorderEnabled() && !headCell.disableReorder;
        const resizable = this.isColumnResizeEnabled() && !headCell.disableResize;
        if (!reorderable && !resizable) return null;
        return (
            <>
                {reorderable && <GridColumnDragGrip />}
                {resizable && (
                    <GridColumnResizeHandle
                        column={headCell}
                        onResizeStart={this.handleColumnResizeStart}
                        onResize={this.handleColumnResize}
                        onResizeEnd={this.handleColumnResizeEnd}
                        onResizeCancel={this.handleColumnResizeCancel}
                        onAutoFit={this.handleColumnAutoFit}
                    />
                )}
            </>
        );
    };

    // One <col> per table column; only data columns carry a width (see
    // sizedColumnsTableSx). The checkbox column ahead of them is held to its
    // content, so spare width goes to the actions column instead. Must match
    // the cells the head, the body rows and the totals render: the <colgroup>
    // maps its columns to cells by position.
    renderColumnGroup(fields, widths) {
        return (
            <colgroup>
                {this.CheckboxCell && <col style={{ width: 1 }} />}
                {fields.map((field, index) => (
                    <col
                        key={field.id}
                        data-col-id={field.id}
                        style={{ width: widths[index] }}
                    />
                ))}
                {this.menuColumnShown && <col />}
            </colgroup>
        );
    }

    renderGridSingleRow(row) {
        const { id } = row;
        const selectedUser = this.state.selected.indexOf(id) !== -1;
        return (
            <TableRow
                hover
                key={id}
                tabIndex={-1}
                role="checkbox"
                selected={selectedUser}
                sx={this.ButtonRow ? { cursor: 'pointer' } : {}}
                onClick={
                    this.ButtonRow
                        ? (event) => {
                            if (event.target.type !== 'checkbox') {
                                this.handleCLickGridRow(event, id);
                            }
                        }
                        : null
                }
            >
                {this.renderCheckboxCell(id, selectedUser)}

                {(this.tableFields || this.getTableFields())
                    .map((field) => {
                        let Cell = Cells[field.cell];
                        if (!Cell) Cell = Cells.Text; // fallback to text cell

                        let cellValue = this.getCellValue(row, field);

                        if (field.customData) {
                            return this.renderCustomGridCell(Cell, row, field);
                        }

                        return (
                            <Cell
                                key={field.id}
                                id={row.id}
                                value={cellValue}
                                field={field}
                                row={row}
                                handleChange={this.handleChangeCell}
                                handleSave={this.handleSaveCell}
                                currentValues={this.state.currentValues}
                                context={this.props.context}
                                allowEdit={
                                    this.allowedPermission('update', adaptedName(this.Name)) &&
                                    this.rowEditable(row)
                                }
                            />
                        );
                    })}
                {this.menuColumnShown !== false && this.renderMenuCell(id)}
            </TableRow>
        );
    }

    renderGridBodyRows(filteredUsers) {
        return (
            <>
                {filteredUsers.map((row) => {
                    return this.renderGridSingleRow(row);
                })}
            </>
        );
    }

    renderCheckboxCell(id, selectedUser) {
        return (
            <TableCell key="checkbox" padding="checkbox">
                <Checkbox
                    checked={selectedUser}
                    onChange={(event) => {
                        this.handleClick(event, id);
                    }}
                />
            </TableCell>
        );
    }

    renderGridBodyNotFound(colSpan = 99999) {
        const {
            searchValue,
            filterActive,
            tableViewportHeight,
            tableViewportWidth
        } = this.state;
        const notFoundSearch = !!searchValue && !filterActive;
        const notFoundFilter = !searchValue && filterActive;
        let icon = <InboxIcon fontSize="inherit" />;
        let title = i18n.t('text.noResults');
        let description = i18n.t('text.noResultsDesc');

        if (notFoundSearch) {
            icon = <SearchOffIcon fontSize="inherit" />;
            title = `${i18n.t('text.noResultsForSearch')}: "${searchValue}"`;
            description = `${i18n.t('text.noResultsSearchDesc')}.`;
        } else if (notFoundFilter) {
            icon = <FilterAltOffIcon fontSize="inherit" />;
            title = i18n.t('text.noResultsByFilter');
            description = i18n.t('text.noResultsByFilterDesc');
        }
        const viewportH = tableViewportHeight || 360;
        const viewportW = tableViewportWidth || 800;

        return (
            <TableRow>
                <TableCell
                    align="center"
                    colSpan={colSpan}
                    sx={{
                        position: 'relative',
                        height: '20px',
                        p: 0
                    }}
                >
                    <Box
                        sx={{
                            position: 'sticky',
                            left: 0,
                            top: 0,
                            width: `${viewportW}px`,
                            minHeight: `${Math.max(viewportH - 48, 240)}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 2
                        }}
                    >
                        <Box sx={{ width: '100%', maxWidth: 560 }}>
                            <EmptyStateInfoCard
                                icon={icon}
                                title={title}
                                description={description}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ height: 0 }} />
                </TableCell>
            </TableRow>
        );
    }

    rowById(id) {
        return this.List.find((item) => item.id === id);
    }

    /**
     * Whether one row may still be edited, or deleted, beyond what the role may do
     * on the grid as a whole. A grid whose rows become final overrides these: an
     * appeal, say, is only editable while pending. The API refuses the rest anyway;
     * this keeps a doomed button off the screen.
     */
    rowEditable(row) {
        return true;
    }

    rowDeletable(row) {
        return true;
    }

    renderCustomMenuItems(id) {
        return <></>;
    }

    renderMenuPopover(itemId) {
        const row = this.rowById(itemId);
        const isPermissionUpdate =
            !!Object.keys(this.state.permissions).length &&
            this.allowedPermission('update') &&
            !this.disableGlobalEditor &&
            this.rowEditable(row);
        const isPermissionDelete =
            !!Object.keys(this.state.permissions).length &&
            this.allowedPermission('delete') &&
            !this.disableGlobalDelete &&
            this.rowDeletable(row);
        const customMenuItems = this.renderCustomMenuItems(itemId);
        return (
            <>
                <Popover
                    key={itemId}
                    open={Boolean(this.state.open)}
                    anchorEl={this.state.open}
                    onClose={(event) => this.handleCloseMenu(event)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    // disablePortal
                    container={document.body}
                    slotProps={{
                        paper: {
                            sx: {
                                "p": 1,
                                "width": 200,
                                '& .MuiMenuItem-root': {
                                    px: 1,
                                    typography: 'body2'
                                }
                            }
                        }
                    }}
                >
                    {customMenuItems}

                    {isPermissionUpdate && (
                        <MenuItem
                            key={itemId + 'edit'}
                            onClick={(event) => this.handleEditItem(event)}
                        >
                            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
                            {i18n.t('buttons.edit')}
                        </MenuItem>
                    )}
                    {isPermissionDelete && (
                        <MenuItem
                            key={itemId + 'delete'}
                            onClick={(event) =>
                                this.handleSingleDeleteConfirmDialogOpen()
                            }
                            sx={{ color: 'error.main' }}
                        >
                            <Iconify
                                icon={'eva:trash-2-outline'}
                                sx={{ mr: 2 }}
                            />
                            {i18n.t('buttons.delete')}
                        </MenuItem>
                    )}
                </Popover>
            </>
        );
    }

    /**
     * How many skeleton rows to draw. Enough to fill the visible table, but
     * never more rows than will actually arrive — a 100-row page size would
     * otherwise put thousands of skeleton elements in the DOM for the sake of
     * rows nobody can see yet.
     */
    skeletonRowCount() {
        const { rowHeight } = getDensity(this.state.density);
        const { tableViewportHeight, rowsPerPage } = this.state;

        // On a refresh this.List still holds the previous page, which is the
        // best guess at what is about to replace it.
        const expected = this.List.length || rowsPerPage;
        const fitsViewport = tableViewportHeight
            ? Math.ceil((tableViewportHeight - rowHeight) / rowHeight)
            : expected;

        return Math.max(3, Math.min(fitsViewport, expected));
    }

    tableRowsLoading(rowCount, cellCount) {
        return [...Array(rowCount)].map((row, index) => (
            <TableRow key={index}>
                {/* The checkbox column the head has, so the skeleton lines up
                    with the head and with the <colgroup> of sized columns. */}
                {this.CheckboxCell && <TableCell padding="checkbox" />}
                {[...Array(cellCount)].map((cell, i) => (
                    <TableCell key={i}>
                        {/* No margins: a text Skeleton is already exactly one
                            line tall, so with the density padding on the cell
                            the row matches a loaded one to the pixel. */}
                        <Skeleton animation="wave" variant="text" />
                    </TableCell>
                ))}
            </TableRow>
        ));
    }

    renderCustom() {
        return <></>;
    }

    render() {
        const permissionsNotLoaded = !this.hasPermissions();
        const loading =
            this.state.isLoading ||
            !this.state.isDataLoading ||
            permissionsNotLoaded;
        const isNotFound = !this.List.length && !loading;

        const currentItemId = this.state.menuClickItemId;

        // Once per render, read by every row and by the head.
        this.menuColumnShown = this.showsMenuColumn();

        const tableFields = this.getTableFields();
        // Once per render, read by every row.
        this.tableFields = tableFields;
        const visibleCellCount = tableFields.length;
        const columnWidths = this.getColumnWidths(tableFields);
        const orderedHead = this.getOrderedHead();

        const currentLength = this.List.length;

        return (
            <>
                {this.renderCustom()}

                {this.renderConfirmDialog()}

                {this.renderDialog()}

                {this.renderModifiedReasonDialog()}

                {this.renderDetailsDialogAbstract()}

                {this.renderSettingsDialog()}
                <GridColumnLayoutUndo
                    undo={this.state.columnLayoutUndo}
                    onUndo={this.handleColumnLayoutUndo}
                    onClose={this.handleColumnLayoutUndoClose}
                />

                {this.renderDateRangePicker()}

                {this.renderCreateExportTaskDialog()}

                {this.renderLockInstanceDialog()}

                <Helmet>
                    <title>{i18n.t(`pages.${this.Name}.title`)}</title>
                </Helmet>

                {/*
                 * The card fills the window instead of sizing to its rows, so a
                 * short result set no longer leaves most of the screen empty and
                 * the pagination bar stays reachable at the bottom rather than
                 * floating under the last row. Toolbar, chips and pagination keep
                 * their natural height; the table between them takes the rest and
                 * scrolls inside itself.
                 */}
                <Container
                    maxWidth={false}
                    sx={{
                        height: `calc(100vh - ${MAIN_CONTENT_OFFSET}px)`,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Cabinet Base: the toolbar is its own card above the
                        table card, so filters read as acting on the table
                        rather than being part of it. */}
                    <Card
                        sx={{
                            flexShrink: 0,
                            mb: 2,
                            borderRadius: (theme) => `${theme.shape.toolbarCard}px`
                        }}
                    >
                        <GridListToolbar
                            Head={this.state.head}
                            handleClickSelect={(event, name) =>
                                this.handleClick(event, name)
                            }
                            numSelected={this.state.selected.length}
                            searchValue={this.state.searchValue}
                            handleSearchData={this.handleSearch}
                            handleSearchKeyDown={this.handleSearchKeyDown}
                            NeedSearch={this.NeedSearch}
                            handleFilterItems={(filter) =>
                                this.handleFilterItems(filter)
                            }
                            filterActive={this.state.filterActive}
                            filterRequest={this.fs.getFlatFiltersByGroup(
                                'prefilterFilter'
                            )}
                            filterEpoch={this.state.filterEpoch}
                            handleTableSettings={() =>
                                this.handleOpenDialogSettings()
                            }
                            onResetColumns={
                                this.isColumnLayoutCustomized()
                                    ? this.handleResetColumnLayout
                                    : undefined
                            }
                            density={this.state.density}
                            onDensityChange={this.handleDensityChange}
                            onDelete={() =>
                                this.handleMultiDeleteConfirmDialogOpen()
                            }
                            deletePermission={this.allowedPermission('delete')}
                            archivePermission={this.allowedArchive()}
                            canViewArchived={controllerSupportsArchive(
                                this.Name
                            )}
                            handleArchive={() =>
                                this.handleArchiveConfirmDialogOpen()
                            }
                            showDatePicker={this.showDatePicker}
                            handleOpenDateRange={this.handleOpenDateRange}
                            rangeValue={this.state.dateRangeValue}
                            limitDateRange={this.limitDateRange}
                            onRangeDate={this.handleChangeRangeDateField}
                            onClearRangeDate={() => this.handleClearDateRange()}
                            onSaveRangeDate={(rangeDate) =>
                                this.handleDateRangeSave(rangeDate)
                            }
                            disableDateRange={this.disableDateRange.disabled}
                            disableDateRangeText={this.disableDateRange.text}
                            exportDataToCsv={() => this.handleExportDataToCsv()}
                            exportButton={this.allowedCsvExportEnableControllers()}
                            params={this.props.params}
                            navigate={this.props.navigate}
                            controller={formatController(this.Name)}
                            toggleArchived={this.toggleArchived}
                            archiveActive={Boolean(this.state.showingArchived)}
                            lockInstance={() => this.handleLockInstance(true)}
                            lockInstancePerm={this.allowedLock()}
                            transferBalancePerm={this.allowedTransferBalance()}
                            transferBalance={(direction) =>
                                this.transferBalance(direction)
                            }
                        />

                        <ActiveFilterChips
                            Head={this.state.head}
                            filters={this.fs.getFlatFiltersByGroup(
                                'prefilterFilter'
                            )}
                            onChange={this.handleRemoveFilters}
                            showingArchived={Boolean(
                                this.state.showingArchived
                            )}
                            onShowActive={() => this.toggleArchived(false)}
                        />
                    </Card>

                    <Card
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            // Without this a flex child refuses to shrink below
                            // its content, so a long table would stretch the card
                            // past the window instead of scrolling.
                            minHeight: 0
                        }}
                    >

                        {/*
                         * Measured here rather than on the TableContainer: this
                         * box is the visible area, so the empty state sizes to
                         * the space on screen. The TableContainer inside grows
                         * with its rows, which is what Scrollbar scrolls.
                         */}
                        <Box
                            ref={this.tableViewportRef}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Scrollbar>
                                <TableContainer
                                    onScroll={this.updateTableViewportSize}
                                    sx={{
                                        overflowX: 'initial',
                                        minWidth: 800
                                    }}
                                >
                                    <Table
                                        stickyHeader
                                        sx={{
                                            ...densityTableSx(this.state.density),
                                            ...(this.isColumnReorderEnabled() &&
                                                gridColumnDndSx),
                                            ...(columnWidths && sizedColumnsTableSx)
                                        }}
                                    >
                                        {columnWidths &&
                                            this.renderColumnGroup(
                                                tableFields,
                                                columnWidths
                                            )}
                                        <GridListHead
                                            order={this.state.sortConfig.direction}
                                            orderBy={this.state.sortConfig.field}
                                            headLabel={orderedHead}
                                            rowCount={this.List.length}
                                            numSelected={this.state.selected.length}
                                            onRequestSort={this.handleRequestSort}
                                            onSelectAllClick={
                                                this.handleSelectAllClick
                                            }
                                            checkboxCell={this.CheckboxCell}
                                            menuColumn={this.menuColumnShown}
                                            role={this.state.currentUserRole}
                                            renderColumnHandle={
                                                this.renderColumnControls
                                            }
                                            getColumnDragProps={
                                                this.getColumnDragProps
                                            }
                                        />
                                        <TableBody>
                                            {loading ? (
                                                this.tableRowsLoading(
                                                    this.skeletonRowCount(),
                                                    visibleCellCount + 1
                                                )
                                            ) : (
                                                <>
                                                    {this.renderGridBodyRows(
                                                        this.List
                                                    )}
                                                    {this.showTotalRow && (
                                                        <GridTotalRow
                                                            head={orderedHead}
                                                            checkboxCell={
                                                                this.CheckboxCell
                                                            }
                                                            data={this.List}
                                                            menuColumn={this.menuColumnShown}
                                                            role={
                                                                this.state
                                                                    .currentUserRole
                                                            }
                                                        />
                                                    )}
                                                </>
                                            )}
                                            {isNotFound &&
                                                this.renderGridBodyNotFound()}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Scrollbar>
                        </Box>

                        {!this.showTotalRow && (
                            <TablePagination
                                sx={{ flexShrink: 0 }}
                                rowsPerPageOptions={
                                    this.getRowsPerPageOptions({
                                        totalCount: this.state.totalCount,
                                        hasNextPage: this.state.hasNextPage,
                                        currentLen: currentLength,
                                        rowsPerPage: this.state.rowsPerPage
                                    }) || []
                                }
                                labelRowsPerPage={i18n.t('text.rowsPerPage')}
                                labelDisplayedRows={({ from, to, count }) => {
                                    if (count === -1) {
                                        return `${from}-${to}`;
                                    }
                                    return `${from}-${to} ${i18n.t(
                                        'text.of'
                                    )} ${count}`;
                                }}
                                component="div"
                                count={this.state.totalCount}
                                rowsPerPage={this.state.rowsPerPage}
                                page={this.state.page}
                                onPageChange={this.handleChangePage}
                                onRowsPerPageChange={
                                    this.handleChangeRowsPerPage
                                }
                                ActionsComponent={(subprops) => (
                                    <TablePaginationActionsWithDisable
                                        count={subprops.count}
                                        page={subprops.page}
                                        rowsPerPage={subprops.rowsPerPage}
                                        onPageChange={subprops.onPageChange}
                                        hasNextPage={this.state.hasNextPage}
                                    />
                                )}
                            />
                        )}
                    </Card>
                </Container>
                {this.renderMenuPopover(currentItemId)}
            </>
        );
    }

    dialogOpen(data) {
        const role = this.state.currentUserRole;
        const visibleFieldSet = getVisibleFieldSet(role);

        const dialogFields = this.state.head.filter((field) => {
            if (!isFieldInVisibleSet(field, visibleFieldSet, role)) return false;

            if (data) {
                return !field.disableEditor || field.displayOnEditForReadOnly;
            }
            return (
                !field.disableEditor ||
                (field.disableEditor && field.displayAdd)
            );
        });
        let dialogData = GridDataAdapter.transform(data, dialogFields);
        this.handleCloseMenu();
        this.setState({ dialogOpen: true, dialogData: dialogData });
    }

    async handleDialogOnSave(data, mode) {
        let result;
        if (mode === 'edit') {
            result = await this.Server.update(data.id, data);
        } else {
            result = await this.Server.add(data);
        }
        if (result.error === 'OK') {
            this.handleDialogOnClose();
        } else {
            this.setState({
                dialogError: formatBackendError(result)
            });
        }
        await this.retrieveData();
    }
    handleOpenModifiedReasonDialog = (data) => {
        this.setState({ modifiedDialogOpen: true, dialogData: data });
    };

    handleSubmitModifiedReasonDialog = async (modifiedReason) => {
        const data = {
            ...this.state.dialogData,
            modifiedReason
        };
        await this.handleDialogOnSave(data, 'edit');
        this.setState({ modifiedDialogOpen: false });
    };

    handleDialogOnClose() {
        this.handleCloseMenu();
        this.setState({
            dialogOpen: false,
            dialogData: null,
            dialogError: null
        });
    }

    renderConfirmDialog() {
        const { confirmDialogOpen, confirmationText } = this.state;
        const actionMap = {
            delete: this.handleDeleteItem,
            archive: this.handleArchive
        };
        return (
            <ConfirmDialog
                dialogOpen={confirmDialogOpen}
                onConfirm={async () => {
                    const action = actionMap[this.state.confirmActionType];
                    if (action) await action();
                    this.setState({ confirmDialogOpen: false });
                }}
                onCancel={() => {
                    this.handleCloseMenu();
                    this.setState({
                        confirmDialogOpen: false,
                        confirmationText: ''
                    });
                }}
                alertText={confirmationText}
                alertTitle={i18n.t('dialogs.confirmationTitle')}
            />
        );
    }

    renderDetailsDialogAbstract() {
        return (
            <DetailsDialogAbstract
                open={this.state.dialogDetailsOpen}
                onClose={() => this.handleCloseDialogDetails()}
                Head={this.state.head.filter((h) => h.displayDialogDetails)}
                context={this.props.context}
                data={this.state.selectedItem}
            />
        );
    }

    renderSettingsDialog() {
        return (
            <TableSettingsDialog
                open={this.state.dialogSettingsOpen}
                onClose={() => this.handleCloseDialogSettings()}
                Head={this.state.head.filter((h) => !h.displayDialogDetails)}
                Name={this.Name}
                onSave={this.handleTableSettingsChange.bind(this)}
                canResetLayout={this.isColumnLayoutCustomized()}
                onResetLayout={
                    this.isColumnLayoutEnabled()
                        ? this.handleResetColumnLayout
                        : undefined
                }
            />
        );
    }

    renderDialog() {
        const needMR = this.shouldShowModifiedReasonDialog();
        return (
            <DialogAbstract
                dialogOpen={this.state.dialogOpen}
                dialogData={this.state.dialogData}
                role={this.state.currentUserRole}
                error={this.state.dialogError}
                Name={this.Name}
                Head={this.state.head}
                Server={this.Server}
                onSave={(data, mode) =>
                    needMR && mode === 'edit'
                        ? this.handleOpenModifiedReasonDialog(data)
                        : this.handleDialogOnSave(data, mode)
                }
                onClose={() => this.handleDialogOnClose()}
            />
        );
    }

    renderDateRangePicker() {
        return (
            <DateTimePickerDialog
                limit={this.limitDateRange}
                dialogOpen={this.state.openDateRange}
                value={this.state.dateRangeValue}
                onClose={() => this.setState({ openDateRange: false })}
                onSave={(dateRange) => this.handleDateRangeSave(dateRange)}
            />
        );
    }
    renderModifiedReasonDialog() {
        return (
            <ModifiedDialog
                open={this.state.modifiedDialogOpen}
                onCancel={() => this.setState({ modifiedDialogOpen: false })}
                onSubmit={async (reason) =>
                    await this.handleSubmitModifiedReasonDialog(reason)
                }
            />
        );
    }
    renderLockInstanceDialog() {
        return (
            <LockInstanceDialog
                open={this.state.lockInstanceDialogOpen}
                server={this.Server}
                onExit={() => this.handleLockInstance(false)}
            />
        );
    }
    renderCreateExportTaskDialog() {
        if (!this.state.createExportTaskDialogOpen) return null;

        const { startDate, endDate } = parseDate(this.state.dateRangeValue);

        const filterRequest = this.fs.getFiltersByGroup('prefilterFilter');
        const searchRequest = this.fs.getFiltersByGroup('search');
        const prefilterRequest = this.fs.getFlatFiltersByGroup('prefilterBase');
        return (
            <CreateExportTaskDialog
                open={this.state.createExportTaskDialogOpen}
                onClose={() =>
                    this.setState({ createExportTaskDialogOpen: false })
                }
                server={this.Server}
                defaultFrom={startDate}
                defaultTo={endDate}
                filterRequest={filterRequest}
                searchRequest={searchRequest}
                prefilterRequest={prefilterRequest}
                onSuccess={(task) => {
                    this.props.context.update({
                        alertError: {
                            message: i18n.t('exportTask.created'),
                            type: 'snackbar',
                            severity: 'success'
                        }
                    });
                }}
            />
        );
    }
}
